import React, { useMemo, useState } from "react";
import { exportResultsToCSV, exportResultsToPDF } from "../../utils/export";
import { formatAdminDateTime } from "../../utils/date";
import { getScorePercentage } from "../../utils/stats";

const PAGE_SIZES = [10, 25, 50];

const AdminResultsTable = ({ results, questions, t, onSelectResult }) => {
  const [search, setSearch] = useState("");
  const [scoreMin, setScoreMin] = useState("");
  const [scoreMax, setScoreMax] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filtered = useMemo(() => {
    return results.filter((res) => {
      const name = (res.candidate?.fullName || "").toLowerCase();
      const phone = res.candidate?.phoneNumber || "";
      const searchLower = search.toLowerCase();
      const matchesSearch = !search || name.includes(searchLower) || phone.includes(search);
      const pct = getScorePercentage(res);
      const matchesMin = scoreMin === "" || pct >= Number(scoreMin);
      const matchesMax = scoreMax === "" || pct <= Number(scoreMax);
      return matchesSearch && matchesMin && matchesMax;
    });
  }, [results, search, scoreMin, scoreMax]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleExportCSV = () => {
    exportResultsToCSV(filtered, questions);
  };

  const handleExportPDF = () => {
    exportResultsToPDF(filtered);
  };

  if (!results.length) {
    return (
      <div className="empty-state">
        <span className="empty-state-icon">📋</span>
        <p>{t.noResultsYet}</p>
      </div>
    );
  }

  return (
    <div className="admin-panel-wrap">
      <div className="admin-panel-toolbar">
        <div className="admin-toolbar-filters">
          <div className="admin-search-wrap">
            <span className="admin-search-icon">🔍</span>
            <input
              type="search"
              className="admin-search-input"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <input
            type="number"
            className="admin-filter-input"
            placeholder={t.minScore}
            value={scoreMin}
            onChange={(e) => { setScoreMin(e.target.value); setPage(1); }}
          />
          <input
            type="number"
            className="admin-filter-input"
            placeholder={t.maxScore}
            value={scoreMax}
            onChange={(e) => { setScoreMax(e.target.value); setPage(1); }}
          />
        </div>
        <div className="admin-toolbar-actions">
          <button type="button" onClick={handleExportCSV} className="admin-btn admin-btn-success">
            ⬇ {t.exportCSV}
          </button>
          <button type="button" onClick={handleExportPDF} className="admin-btn admin-btn-purple">
            ⬇ {t.exportPDF}
          </button>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t.fullName}</th>
              <th>{t.phoneNumber}</th>
              <th>{t.score}</th>
              <th>{t.typingWpm}</th>
              <th>{t.typingAccuracy}</th>
              <th>{t.medicalExp}</th>
              <th>{t.timeTaken}</th>
              <th>{t.submissionDate}</th>
              <th>{t.action}</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((res) => (
              <tr key={res._id} className="admin-table-row" onClick={() => onSelectResult(res)}>
                <td>{res.candidate.fullName}</td>
                <td>{res.candidate.phoneNumber}</td>
                <td>
                  <span className="score-badge">{res.score} / {res.total}</span>
                </td>
                <td>{res.typingTest?.wpm ?? "—"}</td>
                <td>{res.typingTest ? `${res.typingTest.accuracy}%` : "—"}</td>
                <td>{res.candidate.medicalExp}</td>
                <td>{res.timeTakenSeconds ? `${Math.floor(res.timeTakenSeconds / 60)}m ${res.timeTakenSeconds % 60}s` : "—"}</td>
                <td>{formatAdminDateTime(res.timestamp)}</td>
                <td>
                  <button
                    type="button"
                    className="table-action-btn"
                    onClick={(e) => { e.stopPropagation(); onSelectResult(res); }}
                  >
                    {t.viewDetails}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="admin-empty admin-empty-sm">
          <p>{t.noMatchingResults}</p>
        </div>
      )}

      <div className="admin-pagination">
        <span className="pagination-info">
          {t.showing} {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} {t.of} {filtered.length}
        </span>
        <div className="pagination-controls">
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / {t.perPage}</option>)}
          </select>
          <button type="button" className="admin-btn admin-btn-ghost" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
            {t.previous}
          </button>
          <span className="admin-page-num">{currentPage} / {totalPages}</span>
          <button type="button" className="admin-btn admin-btn-ghost" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>
            {t.next}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminResultsTable;
